// src/pages/SellRequests.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { getApp } from 'firebase/app';
import { getFirestore, collection, doc, getDocs, setDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Package, Plus, Clock, CheckCircle, XCircle, MessageCircle, ChevronRight, Trash2, Image as ImageIcon, X } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ConfirmModal from '../components/ConfirmModal';

const MAX_ACTIVE_REQUESTS = 5;
const MAX_COMMENT_LENGTH = 5000;
const MAX_IMAGES = 5;

// Status configuration
const STATUS_CONFIG = {
    pending: { color: '#EAB308', bgColor: 'rgba(234, 179, 8, 0.15)', icon: Clock },
    in_review: { color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.15)', icon: Clock },
    accepted: { color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.15)', icon: CheckCircle },
    rejected: { color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.15)', icon: XCircle },
};

function formatDate(d) {
    if (!d) return '';
    if (typeof d.toDate === 'function') return d.toDate().toLocaleString();
    if (d.seconds) return new Date(d.seconds * 1000).toLocaleString();
    return new Date(d).toLocaleString();
}

export default function SellRequests() {
    const { uid, ready, settings } = useUser();
    const { theme: th, isForest } = useTheme();
    const { t } = useTranslation();
    const db = useMemo(() => getFirestore(getApp()), []);
    const storage = useMemo(() => getStorage(getApp()), []);

    // View state
    const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        listName: '',
        name: '',
        whatsapp: '',
        country: '',
        city: '',
        comment: '',
    });
    const [files, setFiles] = useState([]);
    const [filePreviews, setFilePreviews] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    // Editing state
    const [editing, setEditing] = useState(false);

    // Pagination for list preview in detail view
    const [previewPage, setPreviewPage] = useState(0);
    const PREVIEW_PAGE_SIZE = 10;

    // Board mode toggle for list selection (Converters vs Boards/Electronics)
    const [listMode, setListMode] = useState('converters'); // 'converters' | 'boards'

    // Custom list selector state
    const [listDropdownOpen, setListDropdownOpen] = useState(false);
    const [listSearchQuery, setListSearchQuery] = useState('');
    const [listCounts, setListCounts] = useState({}); // { listName: totalCount }
    const listDropdownRef = React.useRef(null);

    // List data - converters lists
    const converterListsObj = useMemo(() => settings?.lists || {}, [settings?.lists]);
    const converterLists = useMemo(() => {
        const names = new Set(['default', ...Object.keys(converterListsObj || {})]);
        return Array.from(names).sort((a, b) => (a === 'default' ? -1 : a.localeCompare(b)));
    }, [converterListsObj]);

    // List data - boards lists
    const boardListsObj = useMemo(() => settings?.listsBoards || {}, [settings?.listsBoards]);
    const boardLists = useMemo(() => {
        const names = new Set(['default', ...Object.keys(boardListsObj || {})]);
        return Array.from(names).sort((a, b) => (a === 'default' ? -1 : a.localeCompare(b)));
    }, [boardListsObj]);

    // Current list based on mode
    const allLists = listMode === 'boards' ? boardLists : converterLists;

    // Active request count
    const activeCount = useMemo(() => {
        return requests.filter(r => r.status === 'pending' || r.status === 'in_review').length;
    }, [requests]);

    const canCreateMore = activeCount < MAX_ACTIVE_REQUESTS;

    // Fetch requests
    const fetchRequests = useCallback(async () => {
        if (!uid) {
            setRequests([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            // Note: Simple query without orderBy to avoid composite index requirement
            // We sort client-side instead
            const q = query(
                collection(db, 'sellRequests'),
                where('uid', '==', uid)
            );
            const snapshot = await getDocs(q);
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort by createdAt descending (client-side)
            items.sort((a, b) => {
                const aTime = a.createdAt?.seconds || 0;
                const bTime = b.createdAt?.seconds || 0;
                return bTime - aTime;
            });
            setRequests(items);
            console.log('[SellRequests] Fetched', items.length, 'requests for uid:', uid);
        } catch (err) {
            console.error('[SellRequests] Failed to fetch:', err);
            // Log detailed error for Firestore index issues
            if (err.message?.includes('index')) {
                console.error('[SellRequests] INDEX REQUIRED - Create composite index for sellRequests (uid, createdAt)');
            }
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, [db, uid]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // File preview management
    useEffect(() => {
        const urls = files.map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
        setFilePreviews(urls);
        return () => urls.forEach(u => URL.revokeObjectURL(u.url));
    }, [files]);

    // Initialize form with user data
    useEffect(() => {
        if (settings && view === 'create' && !editing) {
            // Check for phone number from profile (works for both phone auth and email auth with optional phone)
            const userPhone = settings.phoneNumber || '';
            setFormData(prev => ({
                ...prev,
                name: `${settings.firstName || ''} ${settings.lastName || ''}`.trim(),
                whatsapp: userPhone,
                country: settings.country || '',
            }));
        }
    }, [settings, view, editing]);

    // Reset list selection when mode changes
    useEffect(() => {
        setFormData(prev => ({ ...prev, listName: '' }));
        setListSearchQuery('');
    }, [listMode]);

    // Fetch list counts when allLists or listMode changes
    useEffect(() => {
        const fetchListCounts = async () => {
            if (!uid) return;
            const collectionPath = listMode === 'boards' ? 'buyListsBoards' : 'buyLists';
            const counts = {};
            for (const name of allLists) {
                try {
                    const listRef = collection(db, collectionPath, uid, name);
                    const snapshot = await getDocs(listRef);
                    // Sum up total field from each item
                    const totalCount = snapshot.docs.reduce((sum, doc) => sum + (doc.data().total || 1), 0);
                    counts[name] = totalCount;
                } catch {
                    counts[name] = 0;
                }
            }
            setListCounts(counts);
        };
        fetchListCounts();
    }, [allLists, uid, db, listMode]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (listDropdownRef.current && !listDropdownRef.current.contains(e.target)) {
                setListDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter lists by search query
    const filteredLists = useMemo(() => {
        const q = listSearchQuery.trim().toLowerCase();
        if (!q) return allLists;
        return allLists.filter(name => name.toLowerCase().includes(q));
    }, [allLists, listSearchQuery]);

    // Validation
    const validateForm = () => {
        if (!formData.listName) {
            setError(t('sellRequest.validationListRequired'));
            return false;
        }
        if (!formData.name.trim()) {
            setError(t('sellRequest.validationNameRequired'));
            return false;
        }
        if (!formData.whatsapp) {
            setError(t('sellRequest.validationWhatsappRequired'));
            return false;
        }
        // Basic phone validation - at least 7 digits
        const digits = formData.whatsapp.replace(/\D/g, '');
        if (digits.length < 7) {
            setError(t('sellRequest.validationWhatsappInvalid'));
            return false;
        }
        if (!formData.country.trim()) {
            setError(t('sellRequest.validationCountryRequired'));
            return false;
        }
        if (!formData.city.trim()) {
            setError(t('sellRequest.validationCityRequired'));
            return false;
        }
        return true;
    };

    // Fetch list items snapshot - handles both converters and boards
    const fetchListItems = async (listName, isBoardMode) => {
        if (!uid || !listName) return [];
        try {
            const collectionPath = isBoardMode ? 'buyListsBoards' : 'buyLists';
            const listRef = collection(db, collectionPath, uid, listName);
            const snapshot = await getDocs(listRef);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (err) {
            console.error('[SellRequests] Failed to fetch list items:', err);
            return [];
        }
    };

    // Convert file to JPEG
    const fileToJpegBlob = async (file) => {
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const fr = new FileReader();
                fr.onerror = reject;
                fr.onload = () => resolve(fr.result);
                fr.readAsDataURL(file);
            });
            const img = await new Promise((resolve, reject) => {
                const im = new Image();
                im.onload = () => resolve(im);
                im.onerror = reject;
                im.src = dataUrl;
            });
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
            return blob || file;
        } catch {
            return file;
        }
    };

    // Submit new request
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!uid) {
            setError(t('sellRequest.signInRequired'));
            return;
        }
        if (!canCreateMore) {
            setError(t('sellRequest.maxRequests', { max: MAX_ACTIVE_REQUESTS }));
            return;
        }
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            // Fetch list items based on list mode (converters vs boards)
            const isBoardMode = listMode === 'boards';
            const listItems = await fetchListItems(formData.listName, isBoardMode);
            if (listItems.length === 0) {
                setError(t('sellRequest.validationListEmpty'));
                setSubmitting(false);
                return;
            }

            const requestId = `${uid}_${Date.now()}`;
            const requestRef = doc(db, 'sellRequests', requestId);

            // Upload images
            const imageUrls = [];
            const imagePaths = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const path = `sellRequests/${uid}/${requestId}_${i}.jpg`;
                const storageRef = ref(storage, path);
                const jpegBlob = await fileToJpegBlob(file);
                await uploadBytes(storageRef, jpegBlob, { contentType: 'image/jpeg' });
                const url = await getDownloadURL(storageRef);
                imageUrls.push(url);
                imagePaths.push(path);
            }

            // Create request document
            await setDoc(requestRef, {
                uid,
                senderUid: uid, // For admin import compatibility
                listId: requestId, // Unique list ID for import
                status: 'pending',
                hasComment: false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),

                listName: formData.listName,
                listData: listItems,
                boardMode: isBoardMode,
                name: formData.name.trim(),
                whatsapp: formData.whatsapp,
                country: formData.country.trim(),
                city: formData.city.trim(),
                comment: formData.comment.trim(),
                imageUrls,
                imagePaths,

                userEmail: settings?.email || '',
                userPhone: settings?.phoneNumber || '',
                userDiscount: settings?.discount || '',
                userPersonalDiscount: settings?.personalDiscount || 0,

                adminComment: '',
                adminCommentUpdatedAt: null,
                resolvedBy: null,
                resolvedAt: null,
            });

            setSuccess(t('sellRequest.submitSuccess'));
            setFormData({ listName: '', name: '', whatsapp: '', country: '', city: '', comment: '' });
            setFiles([]);
            setView('list');
            fetchRequests();
        } catch (err) {
            console.error('[SellRequests] Submit error:', err);
            setError(t('sellRequest.submitError'));
        } finally {
            setSubmitting(false);
        }
    };

    // Delete request
    const handleDelete = async (requestId) => {
        setDeleteConfirmId(null);

        try {
            const request = requests.find(r => r.id === requestId);
            if (request?.imagePaths?.length) {
                for (const path of request.imagePaths) {
                    try {
                        await deleteObject(ref(storage, path));
                    } catch { }
                }
            }
            await deleteDoc(doc(db, 'sellRequests', requestId));
            setRequests(prev => prev.filter(r => r.id !== requestId));
            if (selectedRequest?.id === requestId) {
                setSelectedRequest(null);
                setView('list');
            }
            setSuccess(t('sellRequest.deleteSuccess'));
        } catch (err) {
            console.error('[SellRequests] Delete error:', err);
            setError(t('sellRequest.deleteError'));
        }
    };

    // Open detail view
    const openDetail = async (request) => {
        setSelectedRequest(request);
        setView('detail');
        setEditing(false);
        setPreviewPage(0); // Reset pagination when opening detail

        // Clear hasComment flag if user is viewing a request with new comment
        if (request.hasComment) {
            try {
                const requestRef = doc(db, 'sellRequests', request.id);
                await setDoc(requestRef, { hasComment: false }, { merge: true });
                // Update local state to remove badge
                setRequests(prev => prev.map(r =>
                    r.id === request.id ? { ...r, hasComment: false } : r
                ));
                setSelectedRequest(prev => prev ? { ...prev, hasComment: false } : prev);
            } catch (err) {
                console.error('[SellRequests] Failed to clear hasComment:', err);
            }
        }
    };

    // File picker
    const onPickFiles = (e) => {
        const picked = Array.from(e.target.files || []).filter(f => /^image\//.test(f.type));
        setFiles(prev => {
            const merged = [...prev, ...picked];
            if (merged.length > MAX_IMAGES) {
                setError(t('sellRequest.maxImages', { max: MAX_IMAGES }));
            }
            return merged.slice(0, MAX_IMAGES);
        });
        e.target.value = '';
    };

    const removeFile = (idx) => {
        setFiles(arr => arr.filter((_, i) => i !== idx));
    };

    // Styles
    const cardStyle = {
        backgroundColor: isForest ? '#0f3a30' : th.surface.card,
        border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : th.surface.cardBorder}`,
        boxShadow: isForest ? '0 4px 12px rgba(0,0,0,0.3)' : `0 4px 12px ${th.shadow.md}`,
    };

    const inputStyle = {
        backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : th.input.background,
        border: `1px solid ${isForest ? 'rgba(255,255,255,0.08)' : th.input.border}`,
        color: isForest ? '#e8f5e9' : th.text.primary,
    };

    const tabStyle = (active) => ({
        backgroundColor: active ? th.interactive.primary : (isForest ? 'rgba(26,77,64,0.7)' : th.surface.card),
        color: active ? '#ffffff' : (isForest ? '#e8f5e9' : th.text.primary),
        border: active ? 'none' : `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
    });

    // Not signed in
    if (!ready) {
        return <div className="p-4" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('common.loading')}</div>;
    }
    if (!uid) {
        return <div className="p-4" style={{ color: th.status.error }}>{t('sellRequest.signInRequired')}</div>;
    }

    return (
        <div className="px-0 md:px-0 pt-3" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>
            {/* Tabs */}
            <div className="mb-4 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => { setView('list'); setSelectedRequest(null); setEditing(false); }}
                    className="px-3 py-2 rounded-md font-medium transition-colors"
                    style={tabStyle(view === 'list')}
                >
                    {t('sellRequest.myRequests')} ({requests.length})
                </button>
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={() => { setView('create'); setEditing(false); setError(''); setSuccess(''); }}
                    className="px-3 py-2 rounded-md font-medium transition-colors flex items-center gap-1"
                    style={tabStyle(view === 'create')}
                    disabled={!canCreateMore}
                >
                    <Plus size={16} />
                    {t('sellRequest.newRequest')}
                </button>
            </div>

            {/* Messages */}
            {error && <div className="mb-3 text-sm p-2 rounded" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: th.status.error }}>{error}</div>}
            {success && <div className="mb-3 text-sm p-2 rounded" style={{ backgroundColor: 'rgba(16,185,129,0.1)', color: th.status.success }}>{success}</div>}

            {/* List View */}
            {view === 'list' && (
                <section className="rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
                    {loading ? (
                        <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('common.loading')}</div>
                    ) : requests.length === 0 ? (
                        <div className="text-center py-8">
                            <Package size={48} className="mx-auto mb-3 opacity-40" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }} />
                            <p style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('sellRequest.noRequests')}</p>
                            <p className="text-sm mt-1" style={{ color: isForest ? '#81c784' : th.text.tertiary }}>{t('sellRequest.createFirst')}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {!canCreateMore && (
                                <div className="text-sm p-2 rounded" style={{ backgroundColor: 'rgba(234,179,8,0.1)', color: '#EAB308' }}>
                                    {t('sellRequest.maxRequests', { max: MAX_ACTIVE_REQUESTS })}
                                </div>
                            )}
                            {requests.map(req => {
                                const config = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                                const StatusIcon = config.icon;
                                return (
                                    <div
                                        key={req.id}
                                        onClick={() => openDetail(req)}
                                        className="p-3 rounded-lg cursor-pointer transition-colors hover:opacity-90"
                                        style={{
                                            backgroundColor: isForest ? 'rgba(26,77,64,0.5)' : 'rgba(0,0,0,0.03)',
                                            border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                                        }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                                style={{ backgroundColor: config.bgColor }}
                                            >
                                                <StatusIcon size={20} color={config.color} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium truncate" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>
                                                        {req.listName || 'default'}
                                                    </span>
                                                    {req.hasComment && (
                                                        <span
                                                            className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1"
                                                            style={{ backgroundColor: 'rgba(59,130,246,0.2)', color: '#4caf50' }}
                                                        >
                                                            <MessageCircle size={12} />
                                                            {t('sellRequest.hasComment')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs mt-0.5" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                                                    {formatDate(req.createdAt)} · {t(`sellRequest.status${req.status.charAt(0).toUpperCase() + req.status.slice(1).replace('_', '')}`)}
                                                </div>
                                            </div>
                                            <ChevronRight size={20} style={{ color: isForest ? '#81c784' : th.text.tertiary }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            )}

            {/* Create View */}
            {view === 'create' && (
                <section className="rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* List Type Toggle (Converters vs Boards) */}
                        <div>
                            <label className="block text-sm mb-2" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                {t('sellRequest.listLabel')} *
                            </label>
                            <div className="flex gap-2 mb-3">
                                <button
                                    type="button"
                                    onClick={() => setListMode('converters')}
                                    className="px-4 py-2 rounded-md font-medium transition-colors flex-1"
                                    style={{
                                        backgroundColor: listMode === 'converters' ? th.interactive.primary : (isForest ? 'rgba(26,77,64,0.7)' : th.surface.card),
                                        color: listMode === 'converters' ? '#ffffff' : (isForest ? '#e8f5e9' : th.text.primary),
                                        border: listMode === 'converters' ? 'none' : `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                                    }}
                                >
                                    {t('navigation.converters')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setListMode('boards')}
                                    className="px-4 py-2 rounded-md font-medium transition-colors flex-1"
                                    style={{
                                        backgroundColor: listMode === 'boards' ? th.interactive.primary : (isForest ? 'rgba(26,77,64,0.7)' : th.surface.card),
                                        color: listMode === 'boards' ? '#ffffff' : (isForest ? '#e8f5e9' : th.text.primary),
                                        border: listMode === 'boards' ? 'none' : `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                                    }}
                                >
                                    {t('navigation.electronics')}
                                </button>
                            </div>
                            {allLists.length === 0 ? (
                                <div className="text-sm" style={{ color: th.status.warning }}>{t('sellRequest.noListsAvailable')}</div>
                            ) : (
                                <div ref={listDropdownRef} className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setListDropdownOpen(v => !v)}
                                        className="w-full rounded-lg p-2.5 text-left flex items-center justify-between"
                                        style={inputStyle}
                                    >
                                        <span className={formData.listName ? '' : 'opacity-60'}>
                                            {formData.listName
                                                ? `${formData.listName} (${listCounts[formData.listName] || 0} ${t('sellRequest.itemsInList', { count: listCounts[formData.listName] || 0 }).replace(/^\d+\s*/, '')})`
                                                : t('sellRequest.selectListPlaceholder')}
                                        </span>
                                        <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden className="opacity-70">
                                            <path d="M5.5 7.5l4.5 5 4.5-5" fill="none" stroke="currentColor" strokeWidth="2" />
                                        </svg>
                                    </button>
                                    {listDropdownOpen && (
                                        <div
                                            className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-hidden rounded-xl shadow-lg"
                                            style={{
                                                backgroundColor: isForest ? 'rgba(15,58,48,0.95)' : th.surface.card,
                                                border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                                            }}
                                        >
                                            <div className="p-2" style={{ borderBottom: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}` }}>
                                                <input
                                                    autoFocus
                                                    value={listSearchQuery}
                                                    onChange={(e) => setListSearchQuery(e.target.value)}
                                                    placeholder={t('manageLists.searchLists')}
                                                    className="w-full rounded-lg px-2 py-1.5 text-sm focus:outline-none"
                                                    style={{
                                                        backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : th.input.background,
                                                        border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.input.border}`,
                                                        color: isForest ? '#e8f5e9' : th.text.primary,
                                                    }}
                                                />
                                            </div>
                                            <ul className="max-h-48 overflow-auto py-1">
                                                {filteredLists.map((name) => (
                                                    <li
                                                        key={name}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, listName: name }));
                                                            setListDropdownOpen(false);
                                                            setListSearchQuery('');
                                                        }}
                                                        className="px-3 py-2 cursor-pointer whitespace-nowrap text-sm transition-colors hover:opacity-80"
                                                        style={{
                                                            backgroundColor: name === formData.listName ? (isForest ? 'rgba(15,58,48,0.7)' : 'rgba(0,0,0,0.05)') : 'transparent',
                                                            color: isForest ? '#e8f5e9' : th.text.primary,
                                                        }}
                                                    >
                                                        <span className="font-medium">{name}</span>
                                                        <span className="ml-2 opacity-60">({listCounts[name] || 0} {t('sellRequest.itemsInList', { count: listCounts[name] || 0 }).replace(/^\d+\s*/, '')})</span>
                                                    </li>
                                                ))}
                                                {filteredLists.length === 0 && (
                                                    <li className="px-3 py-2 text-sm" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                                                        {t('converters.noMatches')}
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                            <p className="text-xs mt-1" style={{ color: isForest ? '#81c784' : th.text.tertiary }}>{t('sellRequest.listHelp')}</p>
                        </div>

                        {/* Name */}
                        <div>
                            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                {t('sellRequest.nameLabel')} *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full rounded-lg p-2.5"
                                style={inputStyle}
                                placeholder={t('sellRequest.namePlaceholder')}
                                required
                            />
                        </div>

                        {/* WhatsApp */}
                        <div>
                            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                {t('sellRequest.whatsappLabel')} *
                            </label>
                            <PhoneInput
                                country={'de'}
                                value={formData.whatsapp}
                                onChange={(value) => setFormData(prev => ({ ...prev, whatsapp: value }))}
                                inputStyle={{
                                    width: '100%',
                                    height: '42px',
                                    fontSize: '14px',
                                    ...inputStyle,
                                }}
                                buttonStyle={{
                                    ...inputStyle,
                                    borderRight: 'none',
                                }}
                                dropdownStyle={{
                                    backgroundColor: isForest ? '#0f3a30' : '#ffffff',
                                    color: isForest ? '#e8f5e9' : '#1f2937',
                                }}
                                searchStyle={{
                                    backgroundColor: isForest ? '#0f3a30' : '#ffffff',
                                    color: isForest ? '#e8f5e9' : '#1f2937',
                                    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : '#d1d5db'}`,
                                }}
                                containerClass={isForest ? 'dark-phone-input' : ''}
                            />
                            <p className="text-xs mt-1" style={{ color: isForest ? '#81c784' : th.text.tertiary }}>{t('sellRequest.whatsappHelp')}</p>
                        </div>

                        {/* Country & City */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                    {t('sellRequest.countryLabel')} *
                                </label>
                                <input
                                    type="text"
                                    value={formData.country}
                                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                                    className="w-full rounded-lg p-2.5"
                                    style={inputStyle}
                                    placeholder={t('sellRequest.countryPlaceholder')}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                    {t('sellRequest.cityLabel')} *
                                </label>
                                <input
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                                    className="w-full rounded-lg p-2.5"
                                    style={inputStyle}
                                    placeholder={t('sellRequest.cityPlaceholder')}
                                    required
                                />
                            </div>
                        </div>

                        {/* Comment */}
                        <div>
                            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                {t('sellRequest.commentLabel')}
                            </label>
                            <textarea
                                value={formData.comment}
                                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value.slice(0, MAX_COMMENT_LENGTH) }))}
                                rows={5}
                                className="w-full rounded-lg p-2.5"
                                style={inputStyle}
                                placeholder={t('sellRequest.commentPlaceholder')}
                            />
                            <p className="text-xs mt-1" style={{ color: isForest ? '#81c784' : th.text.tertiary }}>
                                {t('sellRequest.commentLimit', { count: formData.comment.length, max: MAX_COMMENT_LENGTH })}
                            </p>
                        </div>

                        {/* Images */}
                        <div>
                            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                {t('sellRequest.attachImages')}
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                <label
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors"
                                    style={{
                                        backgroundColor: isForest ? 'rgba(26,77,64,0.7)' : th.surface.card,
                                        border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                                    }}
                                >
                                    <ImageIcon size={16} />
                                    <span>{t('sellRequest.attachImages')}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={onPickFiles}
                                        className="sr-only"
                                    />
                                </label>
                                {files.length > 0 && (
                                    <span className="text-sm" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                                        {t('sellRequest.imagesSelected', { count: files.length })}
                                    </span>
                                )}
                            </div>
                            {filePreviews.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {filePreviews.map((p, i) => (
                                        <div key={i} className="relative h-16 w-16">
                                            <img src={p.url} alt={p.name} className="h-full w-full object-cover rounded-md" />
                                            <button
                                                type="button"
                                                onClick={() => removeFile(i)}
                                                className="absolute -top-2 -right-2 rounded-full p-0.5"
                                                style={{
                                                    backgroundColor: isForest ? 'rgba(15,23,42,0.9)' : th.surface.card,
                                                    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                                                }}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs mt-1" style={{ color: isForest ? '#81c784' : th.text.tertiary }}>
                                {t('sellRequest.attachImagesHelp')} · {t('sellRequest.maxImages', { max: MAX_IMAGES })}
                            </p>
                        </div>

                        {/* Submit */}
                        <div className="pt-2 flex gap-2">
                            <button
                                type="button"
                                onClick={() => setView('list')}
                                className="px-4 py-2 rounded-md font-medium"
                                style={{
                                    backgroundColor: isForest ? 'rgba(26,77,64,0.7)' : th.surface.card,
                                    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                                    color: isForest ? '#e8f5e9' : th.text.primary,
                                }}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={submitting || !canCreateMore}
                                className="px-4 py-2 rounded-md text-white font-medium transition-colors"
                                style={{
                                    backgroundColor: submitting ? (isForest ? '#1a4d40' : '#a5d6a7') : th.interactive.primary,
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {submitting ? t('sellRequest.submitting') : t('sellRequest.submit')}
                            </button>
                        </div>
                    </form>
                </section>
            )}

            {/* Detail View */}
            {view === 'detail' && selectedRequest && (
                <section className="rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>
                            {selectedRequest.listName || 'default'}
                        </h2>
                        <div className="flex items-center gap-2">
                            {selectedRequest.status !== 'accepted' && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setDeleteConfirmId(selectedRequest.id)}
                                        className="p-2 rounded-md transition-colors"
                                        style={{ color: th.status.error }}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={() => { setView('list'); setSelectedRequest(null); }}
                                className="p-2 rounded-md transition-colors"
                                style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Status badge */}
                    <div className="mb-4">
                        {(() => {
                            const config = STATUS_CONFIG[selectedRequest.status] || STATUS_CONFIG.pending;
                            const StatusIcon = config.icon;
                            return (
                                <span
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                                    style={{ backgroundColor: config.bgColor, color: config.color }}
                                >
                                    <StatusIcon size={14} />
                                    {t(`sellRequest.status${selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1).replace('_', '')}`)}
                                </span>
                            );
                        })()}
                    </div>

                    {/* Request Info */}
                    <div className="space-y-3 mb-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('sellRequest.nameLabel')}</div>
                                <div style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedRequest.name}</div>
                            </div>
                            <div>
                                <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('sellRequest.whatsappLabel')}</div>
                                <div style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedRequest.whatsapp?.startsWith('+') ? selectedRequest.whatsapp : `+${selectedRequest.whatsapp}`}</div>
                            </div>
                            <div>
                                <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('sellRequest.countryLabel')}</div>
                                <div style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedRequest.country}</div>
                            </div>
                            <div>
                                <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('sellRequest.cityLabel')}</div>
                                <div style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedRequest.city}</div>
                            </div>
                        </div>

                        {/* User Comment */}
                        {selectedRequest.comment && (
                            <div>
                                <div className="text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('sellRequest.commentLabel')}:</div>
                                <div
                                    className="p-3 rounded-lg text-sm whitespace-pre-wrap"
                                    style={{
                                        backgroundColor: isForest ? 'rgba(26,77,64,0.5)' : 'rgba(0,0,0,0.03)',
                                        color: isForest ? '#e8f5e9' : th.text.primary,
                                    }}
                                >
                                    {selectedRequest.comment}
                                </div>
                            </div>
                        )}

                        {/* Images */}
                        {selectedRequest.imageUrls?.length > 0 && (
                            <div>
                                <div className="text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('sellRequest.attachImages')}:</div>
                                <div className="flex flex-wrap gap-2">
                                    {selectedRequest.imageUrls.map((url, i) => (
                                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={url}
                                                alt={`Attachment ${i + 1}`}
                                                className="h-20 w-20 object-cover rounded-lg hover:opacity-80 transition"
                                            />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* List Preview */}
                        <div>
                            <div className="text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                                {t('sellRequest.listPreview')} ({t('sellRequest.itemsInList', { count: selectedRequest.listData?.reduce((sum, item) => sum + (Number(item.total) || Number(item.quantity) || 1), 0) || 0 })}):
                            </div>
                            <div
                                className="p-3 rounded-lg"
                                style={{
                                    backgroundColor: isForest ? 'rgba(26,77,64,0.5)' : 'rgba(0,0,0,0.03)',
                                }}
                            >
                                {selectedRequest.listData?.length > 0 ? (
                                    <>
                                        <ul className="space-y-1 text-sm">
                                            {selectedRequest.listData
                                                .slice(previewPage * PREVIEW_PAGE_SIZE, (previewPage + 1) * PREVIEW_PAGE_SIZE)
                                                .map((item, i) => (
                                                    <li key={previewPage * PREVIEW_PAGE_SIZE + i} style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>
                                                        {item.code || item.name || `Item ${previewPage * PREVIEW_PAGE_SIZE + i + 1}`} {item.total ? `(x${item.total})` : ''}
                                                    </li>
                                                ))}
                                        </ul>
                                        {/* Pagination Controls */}
                                        {selectedRequest.listData.length > PREVIEW_PAGE_SIZE && (
                                            <div className="flex items-center justify-center gap-4 mt-3 pt-2" style={{ borderTop: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewPage(p => Math.max(0, p - 1))}
                                                    disabled={previewPage === 0}
                                                    className="px-2 py-1 rounded text-xs font-medium transition-colors"
                                                    style={{
                                                        backgroundColor: previewPage === 0 ? 'transparent' : (isForest ? 'rgba(76,175,80,0.3)' : 'rgba(76,175,80,0.2)'),
                                                        color: previewPage === 0 ? (isForest ? '#81c784' : '#9ca3af') : (isForest ? '#69f0ae' : '#4caf50'),
                                                        cursor: previewPage === 0 ? 'not-allowed' : 'pointer',
                                                    }}
                                                >
                                                    {t('common.prev')}
                                                </button>
                                                <span className="text-xs" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                                                    {previewPage + 1} / {Math.ceil(selectedRequest.listData.length / PREVIEW_PAGE_SIZE)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewPage(p => Math.min(Math.ceil(selectedRequest.listData.length / PREVIEW_PAGE_SIZE) - 1, p + 1))}
                                                    disabled={previewPage >= Math.ceil(selectedRequest.listData.length / PREVIEW_PAGE_SIZE) - 1}
                                                    className="px-2 py-1 rounded text-xs font-medium transition-colors"
                                                    style={{
                                                        backgroundColor: previewPage >= Math.ceil(selectedRequest.listData.length / PREVIEW_PAGE_SIZE) - 1 ? 'transparent' : (isForest ? 'rgba(76,175,80,0.3)' : 'rgba(76,175,80,0.2)'),
                                                        color: previewPage >= Math.ceil(selectedRequest.listData.length / PREVIEW_PAGE_SIZE) - 1 ? (isForest ? '#81c784' : '#9ca3af') : (isForest ? '#69f0ae' : '#4caf50'),
                                                        cursor: previewPage >= Math.ceil(selectedRequest.listData.length / PREVIEW_PAGE_SIZE) - 1 ? 'not-allowed' : 'pointer',
                                                    }}
                                                >
                                                    {t('common.next')}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('buyingLists.noItemsInList')}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Admin Comment */}
                    <div
                        className="p-3 rounded-lg mb-4"
                        style={{
                            backgroundColor: selectedRequest.adminComment
                                ? (isForest ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)')
                                : (isForest ? 'rgba(26,77,64,0.3)' : 'rgba(0,0,0,0.02)'),
                            border: `1px solid ${selectedRequest.adminComment ? 'rgba(59,130,246,0.3)' : (isForest ? 'rgba(255,255,255,0.05)' : th.surface.cardBorder)}`,
                        }}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <MessageCircle size={14} style={{ color: '#4caf50' }} />
                            <span className="text-sm font-medium" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>
                                {t('sellRequest.adminComment')}
                            </span>
                        </div>
                        {selectedRequest.adminComment ? (
                            <>
                                <div className="text-sm whitespace-pre-wrap" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>
                                    {selectedRequest.adminComment}
                                </div>
                                {selectedRequest.adminCommentUpdatedAt && (
                                    <div className="text-xs mt-2" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                                        {t('sellRequest.adminCommentUpdated', { date: formatDate(selectedRequest.adminCommentUpdatedAt) })}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-sm" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                                {t('sellRequest.noAdminComment')}
                            </div>
                        )}
                    </div>

                    {/* Created date */}
                    <div className="text-xs" style={{ color: isForest ? '#81c784' : th.text.tertiary }}>
                        Created: {formatDate(selectedRequest.createdAt)}
                    </div>
                </section>
            )}

            {/* Delete Sell Request Confirmation Modal */}
            <ConfirmModal
                open={!!deleteConfirmId}
                onClose={() => setDeleteConfirmId(null)}
                onConfirm={() => handleDelete(deleteConfirmId)}
                title={t('common.delete')}
                message={t('sellRequest.deleteConfirm')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                destructive={true}
            />
        </div>
    );
}
